import {bindSessionTaskRecoveryOwner,clearSessionTaskRecovery} from '../lib/sessionTaskRecoveryStorage';
import { createContext, useContext, useEffect, useState, ReactNode, useRef } from 'react';
import {
  authApi,
  ApiRequestError,
  UserProfile,
  clearTokens,
  getAccessToken,
  groupsApi,
  GroupMembership,
  setActiveGroupId
} from '../lib/api';
import { auditApi } from '../lib/api';

interface AuthContextType {
  user: UserProfile | null;
  profile: UserProfile | null;
  groupMemberships: GroupMembership[];
  activeGroupId: string | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  refreshGroups: () => Promise<void>;
  setActiveGroup: (groupId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [groupMemberships, setGroupMemberships] = useState<GroupMembership[]>([]);
  const [activeGroupId, setCurrentActiveGroupId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const groupsRateLimitedUntilRef = useRef(0);
  const groupsRateLimitedLastWarnRef = useRef(0);

  const applyProfile = (userProfile: UserProfile | null) => {
    if(userProfile)bindSessionTaskRecoveryOwner(userProfile.id);else clearSessionTaskRecovery();
    setUser(userProfile);
    setProfile(userProfile);

    const fallbackGroupId = userProfile?.active_group_id || userProfile?.primary_group_id || null;
    setCurrentActiveGroupId(fallbackGroupId);
    setActiveGroupId(fallbackGroupId);
  };

  const refreshGroups = async () => {
    if (Date.now() < groupsRateLimitedUntilRef.current) {
      return;
    }

    try {
      const groupResponse = await groupsApi.getMy();
      setGroupMemberships(groupResponse.memberships || []);

      const resolvedGroupId = groupResponse.active_group_id || null;
      setCurrentActiveGroupId(resolvedGroupId);
      setActiveGroupId(resolvedGroupId);

      groupsRateLimitedUntilRef.current = 0;
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 429) {
        const retryAfterMs = Math.max(3000, error.retryAfterMs || 60000);
        groupsRateLimitedUntilRef.current = Date.now() + retryAfterMs;

        const logNow = Date.now();
        if (logNow - groupsRateLimitedLastWarnRef.current > 5000) {
          console.warn('Refresh groups rate limited. Backing off.', { retryAfterMs });
          groupsRateLimitedLastWarnRef.current = logNow;
        }

        return;
      }

      console.error('Refresh groups error:', error);
      setGroupMemberships([]);
    }
  };

  // Check for existing session on mount
  useEffect(() => {
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    let retryCount = 0;

    const checkAuth = async () => {
      const token = getAccessToken();
      if (!token) {
        clearSessionTaskRecovery();
        setLoading(false);
        return;
      }

      try {
        const { user: userProfile } = await authApi.getProfile();
        applyProfile(userProfile);
        await refreshGroups();
      } catch (error) {
        console.error('Auth check failed:', error);

        const errorMessage = error instanceof Error ? error.message : '';
        const isRateLimited =
          (error instanceof ApiRequestError && error.status === 429)
          || /too many requests/i.test(errorMessage);

        if (isRateLimited) {
          // Preserve tokens for transient limiter responses. Retry once with a long
          // backoff — short multi-retries worsen 429 storms under a shared budget.
          if (retryCount < 1) {
            retryCount += 1;
            const retryAfterMs = error instanceof ApiRequestError
              ? (error.retryAfterMs || 0)
              : 0;
            const retryDelayMs = Math.max(15000, retryAfterMs);

            if (retryTimeout) {
              clearTimeout(retryTimeout);
            }

            retryTimeout = setTimeout(() => {
              void checkAuth();
            }, retryDelayMs);
          }
          return;
        }

        retryCount = 0;
        clearTokens();
        applyProfile(null);
        setGroupMemberships([]);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();

    return () => {
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const response = await authApi.login(email, password);
      applyProfile(response.user);
      await refreshGroups();
      
      // Log successful sign in
      try {
        await auditApi.log('AUTH_SIGNIN', { email, timestamp: new Date().toISOString() });
      } catch (e) {
        console.error('Failed to log sign in:', e);
      }
      
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    const userId = user?.id;
    try {
      // Log sign out before clearing session
      if (userId) {
        await auditApi.log('AUTH_SIGNOUT', { timestamp: new Date().toISOString() });
      }
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      applyProfile(null);
      setGroupMemberships([]);

      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    }
  };

  const refreshProfile = async () => {
    try {
      const { user: userProfile } = await authApi.getProfile();
      applyProfile(userProfile);
    } catch (error) {
      console.error('Refresh profile error:', error);
    }
  };

  const setActiveGroup = async (groupId: string) => {
    const normalizedGroupId = String(groupId || '').trim();
    if (!normalizedGroupId) {
      throw new Error('groupId is required');
    }

    await groupsApi.setActiveGroup(normalizedGroupId);
    setCurrentActiveGroupId(normalizedGroupId);
    setActiveGroupId(normalizedGroupId);
    await Promise.all([refreshProfile(), refreshGroups()]);
  };

  const value: AuthContextType = {
    user,
    profile,
    groupMemberships,
    activeGroupId,
    loading,
    signIn,
    signOut,
    refreshProfile,
    refreshGroups,
    setActiveGroup,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}